import { SELL_API as axios } from '@services/axiosInstance';
import type { ConsentRequest } from '@t/api.types';
import type {
  TimeSlotSearchRequest,
  BookingSlotSearchResponse,
  SlotReservationRequest,
  SlotReservationResponse,
  InstallationDetailsResponse,
  PhoneNumberOptionsRequest,
  PhoneNumberOptionsResponse,
  PhoneBillFileEntry,
} from '@t/checkout.types';

export const getMetadata = async (): Promise<unknown> => {
  const { data } = await axios.get('/metadata');
  return data;
};

export const getInstallationDetails = async (sessionId: string): Promise<InstallationDetailsResponse> => {
  const { data } = await axios.get<InstallationDetailsResponse>(
    `/sessions/${sessionId}/timeslots/installation-details`
  );
  return data;
};

export const searchTimeSlots = async (
  sessionId: string,
  payload: TimeSlotSearchRequest
): Promise<BookingSlotSearchResponse> => {
  const { data } = await axios.post<BookingSlotSearchResponse>(`/sessions/${sessionId}/timeslots/search`, payload);
  return data;
};

export const reserveTimeSlot = async (
  sessionId: string,
  payload: SlotReservationRequest
): Promise<SlotReservationResponse> => {
  const { data } = await axios.post<SlotReservationResponse>(`/sessions/${sessionId}/timeslots/reserve`, payload);
  return data;
};

export const releaseTimeSlot = async (shoppingCartId: number): Promise<void> => {
  await axios.post('/timeslots/release-slot', { shoppingCartId });
};

export const releasePortReservation = async (shoppingCartId: number): Promise<void> => {
  await axios.post('/portReservation/release-port', { shoppingCartId });
};

export const getPhoneOptions = async (sessionId: string): Promise<PhoneNumberOptionsResponse> => {
  const { data } = await axios.get<PhoneNumberOptionsResponse>(`/sessions/${sessionId}/phoneOptions`);
  return data;
};

export const upsertPhoneOptions = async (
  sessionId: string,
  payload: PhoneNumberOptionsRequest
): Promise<PhoneNumberOptionsResponse> => {
  const { data } = await axios.post<PhoneNumberOptionsResponse>(`/sessions/${sessionId}/phoneOptions`, payload);
  return data;
};

export const uploadPhoneBill = async (sessionId: string, file: File): Promise<PhoneBillFileEntry> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post<PhoneBillFileEntry>(`/sessions/${sessionId}/phoneOptions/bill`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const removePhoneBill = async (sessionId: string, fileId: string): Promise<PhoneNumberOptionsResponse> => {
  const { data } = await axios.delete<PhoneNumberOptionsResponse>(`/sessions/${sessionId}/phoneOptions/bill/${fileId}`);
  return data;
};

export const saveConsent = async (sessionId: string, payload: ConsentRequest): Promise<unknown> => {
  const { data } = await axios.post(`/sessions/${sessionId}/consent`, payload);
  return data;
};

export const submitOrder = async (sessionId: string): Promise<unknown> => {
  const { data } = await axios.post(`/sessions/${sessionId}/shopping-cart/submit`);
  return data;
};

export const downloadOrderSummary = async (sessionId: string): Promise<Blob> => {
  const { data } = await axios.get(`/sessions/${sessionId}/shopping-cart/summary`, {
    responseType: 'blob',
  });
  return data;
};
